// PRD 06 §2.1 — 3 stat cards. Per ADM.002 the Total Items / Total Questions
// counts are scoped to the CURRENTLY SELECTED disease row. FEATURE_025 — every row
// shares one module set, so the count takes the placed items that belong to the
// selected row (`isVisible`, matched by id). Items NOT in the dictionary are
// silently skipped.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Module, ProItemRef } from '../types/pro-deployment.types';

interface StatsRowProps {
  modules: Module[];
  items: ProItemRef[];
  monitoringWeeks: number;
  // FEATURE_025 — whether an item belongs to the selected dropdown row. Omitted → every item counts.
  isVisible?: (item: ProItemRef) => boolean;
}

export function StatsRow({ modules, items, monitoringWeeks, isVisible }: StatsRowProps) {
  const { t } = useTranslation('pro-deployment');

  const { totalItems, totalQuestions } = useMemo(() => {
    const itemByPid = new Map(items.map((i) => [i.pid, i]));
    let totalItems = 0;
    let totalQuestions = 0;
    for (const mod of modules) {
      for (const pid of mod.itemIds) {
        const item = itemByPid.get(pid);
        if (!item) continue;
        if (isVisible && !isVisible(item)) continue;
        totalItems += 1;
        totalQuestions += item.questionCount;
      }
    }
    return { totalItems, totalQuestions };
  }, [modules, items, isVisible]);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--space-4)',
        marginBottom: 'var(--space-6)',
      }}
    >
      <StatCard value={totalItems} label={t('stats.totalItems')} />
      <StatCard value={totalQuestions} label={t('stats.totalQuestions')} />
      <StatCard
        value={monitoringWeeks}
        suffix={t('scope.weeksUnit')}
        label={t('stats.monitoringPeriod')}
      />
    </div>
  );
}

interface StatCardProps {
  value: number;
  label: string;
  suffix?: string;
}

function StatCard({ value, label, suffix }: StatCardProps) {
  return (
    <div
      className="card"
      style={{
        flex: '1 1 180px',
        minWidth: 0,
        padding: 'var(--space-4) var(--space-6)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 700,
          color: 'var(--foreground)',
        }}
      >
        {value}
        {suffix ? (
          <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 400 }}>{suffix}</span>
        ) : null}
      </div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--muted-foreground)' }}>
        {label}
      </div>
    </div>
  );
}
